import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { t } from '../../lib/i18n';
import {
  DEPOSITS_QUERY_KEY,
  FINANCE_CLIENTS_QUERY_KEY,
  JOURNAL_PAGE_SIZE,
  PRICING_RULES_QUERY_KEY,
  REVENUE_MONTHS,
  fetchDeposits,
  fetchFinanceClients,
  fetchFinanceSummary,
  fetchJournalPage,
  fetchPricingRules,
  fetchRevenue,
  financeSummaryQueryKey,
  isAccessError,
  journalQueryKey,
  revenueQueryKey,
} from './api';
import { DepositsTable } from './components/deposits-table';
import { FinanceClientsTable } from './components/finance-clients-table';
import { FinanceHeader } from './components/finance-header';
import { FinanceStatCards } from './components/finance-stat-cards';
import { FinanceTabs } from './components/finance-tabs';
import {
  FinanceViewSwitch,
  financeViewPanelId,
  financeViewTabId,
  type FinanceView,
} from './components/finance-view-switch';
import { IssueInvoiceDrawer } from './components/issue-invoice-drawer';
import { PanelError, PanelLoading } from './components/panel-state';
import { RevenueCard } from './components/revenue-card';
import { TopUpRequestsPanel } from './components/topup-requests-panel';
import { TransactionsTable } from './components/transactions-table';
import { UpcomingReceivables } from './components/upcoming-receivables';
import { financePeriodLabel, revenueWindowLabel } from './labels';
import type { FinancePeriod, FinanceTab } from './types';
import {
  clientInTab,
  clientMatchesQuery,
  clientRollupStats,
  formatChf,
  periodMonthIndices,
  revenueBars,
  revenueTotals,
  tabCounts,
  upcomingReceivables,
} from './utils';

/**
 * «Фінанси» — `Redesign-D-Admin{,-Mobile}`.
 *
 * Who owes us what, what we earned, and what lands next — in that order,
 * because that is the order of the operator's questions. The four counters
 * answer "how much is outstanding", the table answers "from whom", and the
 * right-hand column answers "and how are we actually doing".
 *
 * ── Live data since the phase-6 cutover ─────────────────────────────────────
 * Every figure on this screen comes from `apps/api`'s admin-finance surface.
 * The fixture module is deleted and the preview env flag that hid the route is
 * gone with it — in that order, so the flag outlived the fake money rather than
 * the other way round.
 *
 * ── Five queries, five failure domains ──────────────────────────────────────
 * The panels do not share a request. A journal that 500s must leave the
 * balances table standing, because chasing an overdue client does not depend on
 * the ledger, and one folded promise would have taken the whole screen down
 * with its slowest member. Each section therefore renders its own loading,
 * error and EMPTY state — see `components/panel-state.tsx` for why empty is a
 * first-class state and not a variant of loading.
 *
 * ── The one screen-wide failure ─────────────────────────────────────────────
 * 401/403 is not a panel's problem: the operator is anonymous or lacks the
 * admin role, so nothing on the page can render and the shared access-denied
 * screen answers instead. It is detected by STATUS, before any body is read —
 * `apps/api` installs no global exception filter, so a guard-ladder refusal
 * carries a STRING on `error` where a handler's refusal carries an object, and
 * a reader that went for `error.code` first would turn "not signed in" into
 * "unknown failure". See `financeErrorCode` in api.ts.
 */
/**
 * The balances column. The clients table needs 960 px, so below `2xl` the
 * section takes the whole row and the revenue aside wraps beneath it; a
 * 620 px basis let the aside share a 1280 px row and squeezed the table
 * (BUG-48).
 */
export const BALANCES_SECTION_CLASS =
  'flex min-w-0 basis-full flex-col gap-3 2xl:flex-[1_1_620px]';

/** `?view=` → the view the screen opens on; anything unknown is the default. */
export function parseFinanceView(value: unknown): FinanceView {
  return value === 'deposits' ? 'deposits' : 'balances';
}

export function FinancePage() {
  const routeSearch = useSearch({ strict: false }) as { view?: unknown };
  const [view, setView] = useState<FinanceView>(() => parseFinanceView(routeSearch.view));
  const [tab, setTab] = useState<FinanceTab>('all');
  const [period, setPeriod] = useState<FinancePeriod>('month');
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const clientsQuery = useQuery({
    queryKey: FINANCE_CLIENTS_QUERY_KEY,
    queryFn: fetchFinanceClients,
  });
  const summaryQuery = useQuery({
    queryKey: financeSummaryQueryKey(period),
    queryFn: () => fetchFinanceSummary(period),
  });
  const revenueQuery = useQuery({
    queryKey: revenueQueryKey(REVENUE_MONTHS),
    queryFn: () => fetchRevenue(REVENUE_MONTHS),
  });
  const journalQuery = useQuery({
    queryKey: journalQueryKey(JOURNAL_PAGE_SIZE),
    queryFn: () => fetchJournalPage(JOURNAL_PAGE_SIZE),
  });

  /**
   * «Депозити й режими» — fetched only once the operator opens that view: it
   * costs the API two indexed reads per listed client, and the balances view
   * (the default) reads none of it.
   */
  const depositsQuery = useQuery({
    queryKey: DEPOSITS_QUERY_KEY,
    queryFn: fetchDeposits,
    enabled: view === 'deposits',
  });

  /**
   * The tariff the invoice drawer quotes. A SEPARATE query from the finance
   * surface on purpose: it is a different module with a different cache
   * lifetime, and it is shared with the tariffs screen through one query key.
   */
  const rulesQuery = useQuery({
    queryKey: PRICING_RULES_QUERY_KEY,
    queryFn: fetchPricingRules,
  });

  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const months = useMemo(() => revenueQuery.data ?? [], [revenueQuery.data]);

  const stats = useMemo(() => clientRollupStats(clients), [clients]);
  const counts = useMemo(() => tabCounts(clients), [clients]);
  const receivables = useMemo(() => upcomingReceivables(clients), [clients]);

  /**
   * Search first, then the tab — the counts above come from `tabCounts` over
   * the WHOLE list, so they keep saying how many clients are in each slice
   * while a search narrows what is on screen. Folding search into the counts
   * too would make a tab read "0" while its label is the reason you are here.
   */
  const rows = useMemo(
    () =>
      clients.filter(
        (client) => clientInTab(client.status, tab) && clientMatchesQuery(client, search),
      ),
    [clients, tab, search],
  );

  const monthIndices = useMemo(() => periodMonthIndices(months, period), [months, period]);
  const revenue = useMemo(() => revenueTotals(months, monthIndices), [months, monthIndices]);
  const bars = useMemo(() => revenueBars(months, monthIndices), [months, monthIndices]);
  /**
   * The revenue card's OWN label, read off the SAME `monthIndices` that
   * `revenue`/`bars` are folded from — never `periodLabel`. See the docblock
   * on `revenueWindowLabel`: `periodLabel` names the calendar period the
   * summary card's figure covers, and that is not always the same span as the
   * trailing-window approximation charted here.
   */
  const revenueLabel = useMemo(
    () => revenueWindowLabel(months, monthIndices),
    [months, monthIndices],
  );

  const summary = summaryQuery.data;
  const periodLabel = financePeriodLabel(period, summary?.computedAt);

  /**
   * 401/403 anywhere means the whole screen is unavailable to this operator.
   * Checked across every query rather than one: each endpoint is guarded
   * independently, and a role change mid-session shows up on whichever refetches
   * first.
   */
  if (
    [clientsQuery, summaryQuery, revenueQuery, journalQuery, rulesQuery, depositsQuery].some((query) =>
      isAccessError(query.error),
    )
  ) {
    return <AccessDenied />;
  }

  /**
   * A failed rule-set fetch is an ERROR STATE, never a fallback. Rendering the
   * drawer against `DEFAULT_PRICING_RULES` would quote an owner-unsigned grid
   * on an invoice — the exact failure `LEGACY_PRICING_PLANS` exists to prevent
   * on the server — and it would be indistinguishable from a working screen.
   * It gates the DRAWER only: the rest of the page reads no tariff at all, and
   * blanking the balances table because a pricing document is unreadable would
   * hide the debt an operator came here to chase.
   */
  const canQuote = !rulesQuery.isError && Boolean(rulesQuery.data);

  /**
   * Why «Виставити рахунок» is unavailable, as a SENTENCE rather than a
   * boolean. An unreadable tariff is an incident and an empty client list is a
   * Tuesday; one shrug for both is how the first gets mistaken for the second.
   */
  const issueBlockedReason = rulesQuery.isError
    ? t('adminFinance.rulesError')
    : !canQuote || clients.length === 0
      ? t('adminFinance.issueUnavailable')
      : null;

  // Every number is resolved to a literal before `t` sees it: the admin `t`
  // echoes a raw `{token}` for an undefined param, which would print
  // "{receivable} до отримання" straight into the header. Every figure is CHF
  // (owner rule 01) — one formatter, one unit.
  const eyebrow = summary
    ? t('adminFinance.eyebrow', {
        period: periodLabel,
        receivable: formatChf(summary.receivableChf),
        deposits: formatChf(summary.depositsHeldChf),
        revenue: formatChf(summary.revenueChf),
      })
    : t('adminFinance.eyebrowPending');

  const toggleRow = (userId: string) =>
    setExpanded((current) => ({ ...current, [userId]: !current[userId] }));

  return (
    <>
      <FinanceHeader
        eyebrow={eyebrow}
        period={period}
        periodNow={summary?.computedAt}
        onPeriodChange={setPeriod}
        onIssueInvoice={() => setDrawerOpen(true)}
        issueBlockedReason={issueBlockedReason}
      />

      <FinanceViewSwitch active={view} onChange={setView} />

      {summaryQuery.isPending ? (
        <PanelLoading />
      ) : summaryQuery.isError ? (
        <PanelError error={summaryQuery.error} fallbackKey="adminFinance.error.summary" />
      ) : (
        <FinanceStatCards
          summary={summaryQuery.data}
          stats={stats}
          periodLabel={periodLabel}
          active={tab}
          onSelect={(next) => {
            // The cards filter the balances table, so a click from the
            // deposits view goes to where its filter is visible.
            setTab(next);
            setView('balances');
          }}
        />
      )}

      {view === 'deposits' ? (
        <div
          role="tabpanel"
          id={financeViewPanelId('deposits')}
          aria-labelledby={financeViewTabId('deposits')}
          className="flex flex-col gap-3 leading-[normal]"
        >
          <TopUpRequestsPanel />
          {depositsQuery.isPending ? (
            <PanelLoading />
          ) : depositsQuery.isError ? (
            <PanelError error={depositsQuery.error} fallbackKey="adminFinance.deposits.error.load" />
          ) : (
            <DepositsTable rows={depositsQuery.data} clients={clients} />
          )}
          <p className="text-[12px] leading-[1.5] text-cf-ink-subtle">
            {t('adminFinance.deposits.footnote')}
          </p>
        </div>
      ) : (
        <div
          role="tabpanel"
          id={financeViewPanelId('balances')}
          aria-labelledby={financeViewTabId('balances')}
          className="flex flex-col gap-3 lg:gap-5"
        >
        <div className="flex flex-wrap items-start gap-5 leading-[normal]">
          {/* ── Balances ────────────────────────────────────────────────── */}
          <section className={BALANCES_SECTION_CLASS}>
            <div className="flex flex-wrap items-center gap-2">
              <FinanceTabs active={tab} counts={counts} onChange={setTab} />

              <label className="flex h-10 min-w-0 flex-[1_1_200px] items-center gap-2.5 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 lg:max-w-[320px]">
                <Search
                  size={16}
                  strokeWidth={1.75}
                  className="shrink-0 text-cf-ink-subtle"
                  aria-hidden
                />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('adminFinance.search')}
                  aria-label={t('adminFinance.searchAria')}
                  className="min-w-0 flex-1 border-0 bg-transparent px-0.5 text-[14px] outline-none"
                />
              </label>
            </div>

            {clientsQuery.isPending ? (
              <PanelLoading />
            ) : clientsQuery.isError ? (
              <PanelError error={clientsQuery.error} fallbackKey="adminFinance.error.clients" />
            ) : (
              <FinanceClientsTable
                clients={rows}
                /* An empty PLATFORM and an empty FILTER are different facts and
                   read as different sentences — the first is the normal first-run
                   state, the second is a search the operator can undo. */
                emptyKey={
                  clients.length === 0 ? 'adminFinance.table.emptyPlatform' : 'adminFinance.table.empty'
                }
                expanded={expanded}
                onToggle={toggleRow}
              />
            )}

            <p className="hidden text-[12px] leading-[1.5] text-cf-ink-subtle lg:block">
              {t('adminFinance.footnote')}
            </p>
          </section>

          {/* ── Revenue + what lands next ───────────────────────────────── */}
          <aside className="flex w-full flex-[1_1_320px] flex-col gap-4 lg:w-auto lg:max-w-[400px]">
            {revenueQuery.isPending ? (
              <PanelLoading />
            ) : revenueQuery.isError ? (
              <PanelError error={revenueQuery.error} fallbackKey="adminFinance.error.revenue" />
            ) : (
              <RevenueCard
                period={period}
                periodNow={summary?.computedAt}
                onPeriodChange={setPeriod}
                windowLabel={revenueLabel}
                totals={revenue}
                bars={bars}
                hasData={months.length > 0}
              />
            )}

            {clientsQuery.isPending ? (
              <PanelLoading />
            ) : clientsQuery.isError ? (
              <PanelError error={clientsQuery.error} fallbackKey="adminFinance.error.receivables" />
            ) : (
              <UpcomingReceivables items={receivables} />
            )}
          </aside>
        </div>

        {journalQuery.isPending ? (
          <PanelLoading />
        ) : journalQuery.isError ? (
          <PanelError error={journalQuery.error} fallbackKey="adminFinance.error.journal" />
        ) : (
          <TransactionsTable transactions={journalQuery.data.entries} />
        )}

        </div>
      )}

      {drawerOpen && canQuote && rulesQuery.data && (
        <IssueInvoiceDrawer
          onClose={() => setDrawerOpen(false)}
          clients={clients}
          rules={rulesQuery.data}
        />
      )}
    </>
  );
}
